import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RequestWithRelationsFromRequestsList } from '@/models/request';

export interface RequestSelection {
  selectedIds: Set<string>;
  toggle: (requestId: string) => void;
  /** Applies the new state of `requestId` to every request between it and the last toggled one */
  toggleRange: (requestId: string) => void;
  selectAll: (requestIds: string[]) => void;
  replace: (requestIds: Iterable<string>) => void;
  clear: () => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

const EMPTY_SELECTION: Set<string> = new Set();

/**
 * Page-scoped selection of requests on the Requests index.
 *
 * The selection is reset whenever `resetKey` changes: the owner passes a serialized version of the
 * list query params so that changing tab, page, search, filters or sort clears the selection.
 * Selected requests that disappear from the page (e.g. after a refetch) are dropped.
 */
export const useRequestSelection = ({
  requests,
  resetKey,
}: {
  requests: RequestWithRelationsFromRequestsList[];
  resetKey: string;
}): RequestSelection => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(EMPTY_SELECTION);

  // Last toggled request, used as the start of a range selection
  const anchorIdRef = useRef<string | null>(null);

  useEffect(() => {
    anchorIdRef.current = null;
    setSelectedIds((current) => (current.size === 0 ? current : EMPTY_SELECTION));
  }, [resetKey]);

  const pageIds = useMemo(() => requests.map((request) => request.id), [requests]);

  // A refetch of the same query can replace the page contents without changing `resetKey`:
  // drop the selected requests that are no longer displayed
  useEffect(() => {
    setSelectedIds((current) => {
      const pageIdsSet = new Set(pageIds);
      const next = new Set([...current].filter((id) => pageIdsSet.has(id)));

      return next.size === current.size ? current : next;
    });
  }, [pageIds]);

  const isAllSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const isIndeterminate = !isAllSelected && pageIds.some((id) => selectedIds.has(id));

  const toggle = useCallback((requestId: string) => {
    anchorIdRef.current = requestId;
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }

      return next;
    });
  }, []);

  const toggleRange = useCallback(
    (requestId: string) => {
      const anchorIndex = anchorIdRef.current ? pageIds.indexOf(anchorIdRef.current) : -1;
      const index = pageIds.indexOf(requestId);

      if (anchorIndex === -1 || index === -1) {
        toggle(requestId);

        return;
      }

      const rangeIds = pageIds.slice(
        Math.min(anchorIndex, index),
        Math.max(anchorIndex, index) + 1
      );

      setSelectedIds((current) => {
        const shouldSelect = !current.has(requestId);
        const next = new Set(current);

        rangeIds.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));

        return next;
      });
      anchorIdRef.current = requestId;
    },
    [pageIds, toggle]
  );

  const replace = useCallback((requestIds: Iterable<string>) => {
    anchorIdRef.current = null;
    setSelectedIds(new Set(requestIds));
  }, []);

  // The selection is page-scoped: selecting all replaces the whole selection with the page ids
  const selectAll = replace;

  const clear = useCallback(() => {
    anchorIdRef.current = null;
    setSelectedIds((current) => (current.size === 0 ? current : EMPTY_SELECTION));
  }, []);

  return {
    selectedIds,
    toggle,
    toggleRange,
    selectAll,
    replace,
    clear,
    isAllSelected,
    isIndeterminate,
  };
};
